import { AxiosError } from 'axios';
import { problemDetail } from '@/shared/api/problem';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';

/**
 * What to show when a list fails to load.
 *
 * ⚠️ A 403 must not offer a retry.
 *
 * Every page here showed "something went wrong — try again" for any failure, and a signed-in
 * administrator opening the committee queue got exactly that. But an administrator is deliberately not
 * a committee member (§23.2: voting is the committee's act), so the 403 was correct and retrying could
 * never fix it. The operator is left clicking a button that is guaranteed to fail, with no way to tell
 * that the panel is working as designed.
 *
 * Found by opening the page in a browser as a real signed-in user. Every test had stubbed a 200.
 */
export function QueryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useLocale();

  const forbidden = error instanceof AxiosError && error.response?.status === 403;

  if (forbidden) {
    return (
      <p role="alert" className="rounded-md border border-border bg-surface px-4 py-6 text-center text-sm text-muted-foreground">
        {/* The API's own explanation when it gave one — it is more specific than anything this
            component could say (§13). */}
        {problemDetail(error) ?? t.errors.forbidden}
      </p>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <p role="alert" className="text-sm text-danger">{t.errors.generic}</p>
      <Button variant="ghost" onClick={onRetry}>
        {t.errors.retry}
      </Button>
    </div>
  );
}
